import React, { useState, useMemo } from "react";
import styles from "./consumption-pattern.module.scss";
import { DateRangePreset } from "../../../components/date-range/daterange-picker.types";
import {
	formatDateRange,
	getLastNDays,
	getThisWeek,
	getThisMonth,
} from "../../../components/date-range/date-range.utils";
import { DateRangePicker, DateRangeButton } from "../../../components";
import { useGetSolarConsumptionTotalQuery } from "../../../../../service/vending-service";
import {
	SolarEnergyGeneratedIcon,
	SolarEnergyConsumedIcon,
	SolarEnergyStoredIcon,
	SolarEnergyTransmittedIcon,
	DateRangeChevronIcon,
} from "../../../../../assets/icons";

interface ConsumptionPatternProps {
	subscriptionId: string;
	isReady: boolean;
}

export const ConsumptionPattern: React.FC<ConsumptionPatternProps> = ({
	subscriptionId,
	isReady,
}) => {
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [selectedPeriod, setSelectedPeriod] = useState("Today");
	const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(
		getLastNDays(1)
	);

	const apiParams = useMemo(
		() => ({
			From: dateRange.from.toISOString(),
			To: dateRange.to.toISOString(),
			PayAsYouGoSubscriptionId: subscriptionId,
		}),
		[dateRange, subscriptionId]
	);

	const {
		data: apiResponse,
		isLoading,
		isFetching,
	} = useGetSolarConsumptionTotalQuery(apiParams, { skip: !isReady });

	const presets: DateRangePreset[] = [
		{ label: "Today", getValue: () => getLastNDays(1) },
		{ label: "This Week", getValue: getThisWeek },
		{ label: "This Month", getValue: getThisMonth },
	];

	const handleApply = (result: any) => {
		setDateRange({ from: result.from, to: result.to });
		if (result.type === "preset" && result.presetLabel) {
			setSelectedPeriod(result.presetLabel);
		} else {
			setSelectedPeriod(formatDateRange(result.from, result.to));
		}
	};

	const metrics = useMemo(
		() => [
			{
				label: "Generated",
				value: apiResponse?.generated ?? 0,
				icon: <SolarEnergyGeneratedIcon />,
			},
			{
				label: "Consumed",
				value: apiResponse?.consumed ?? 0,
				icon: <SolarEnergyConsumedIcon />,
			},
			{
				label: "Stored",
				value: apiResponse?.stored ?? 0,
				icon: <SolarEnergyStoredIcon />,
			},
			{
				label: "Transmitted to Grid",
				value: apiResponse?.transmitted ?? 0,
				icon: <SolarEnergyTransmittedIcon />,
			},
		],
		[apiResponse]
	);

	const loading = isLoading || isFetching;

	const renderSkeleton = () => (
		<div className={styles.grid}>
			{[0, 1, 2, 3].map((i) => (
				<div key={i} className={styles.skeletonCard}>
					<div className={styles.skeletonCircle} />
					<div className={styles.skeletonLine} />
					<div className={styles.skeletonLineSmall} />
				</div>
			))}
		</div>
	);

	return (
		<>
			<div className={styles.container}>
				<div className={styles.header}>
					<span className={styles.title}>Consumption Pattern</span>
					<DateRangeButton
						selectedLabel={selectedPeriod}
						onClick={() => setIsPickerOpen(true)}
						icon={<DateRangeChevronIcon />}
					/>
				</div>

				{loading ? (
					renderSkeleton()
				) : (
					<div className={styles.grid}>
						{metrics.map((metric) => (
							<div key={metric.label} className={styles.metricCard}>
								<div className={styles.metricContent}>
									<div className={styles.iconCircle}>{metric.icon}</div>
									<div className={styles.metricValue}>
										<span className={styles.valueNumber}>
											{metric.value.toFixed(metric.value % 1 === 0 ? 0 : 2)}
										</span>
										<span className={styles.valueUnit}>kwh</span>
									</div>
									<span className={styles.metricLabel}>{metric.label}</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<DateRangePicker
				title="Date Filter"
				subtitle="Filter results by specific time periods"
				presets={presets}
				onApply={handleApply}
				onClose={() => setIsPickerOpen(false)}
				isOpen={isPickerOpen}
				initialFrom={dateRange.from}
				initialTo={dateRange.to}
				showCustomOption={false}
			/>
		</>
	);
};
